import { isEmail, isUUID } from 'class-validator';
import { inject, injectable } from 'inversify';
import React from 'react';

import { Authority } from '@project44/ui-utils';

import { ReactHookServicePort } from '../../../../shared/core/port/React.hook.service.port';
import { TenantFactory } from '../../../Tenant/infrastructure/factories/Tenant.factory';
import {
  IUserHookFactory,
  TUIUserTenantAgnosticRoles,
  TUIUserTenantSpecificRoles,
  TUseUserTenantAgnosticRoles,
  TUseUserTenantSpecificRoles,
} from '../../domain/interfaces';
import { UserMapper } from '../../domain/mapper/User.mapper';
import { UserFactory } from './User.factory';

@injectable()
export class UserHookFactory implements IUserHookFactory {
  constructor(
    @inject(ReactHookServicePort)
    private readonly reactHookServicePort: ReactHookServicePort,

    @inject(UserFactory) private readonly userFactory: UserFactory,

    @inject(TenantFactory) private readonly tenantFactory: TenantFactory,
  ) {}

  public composeUseUserTenantSpecificRoles(): TUseUserTenantSpecificRoles {
    return (payload) => {
      const { userPrincipal } = payload;
      const user = this.userFactory.createUserDto(
        payload.user?.toSerializedModel(),
      );
      const tenant = this.tenantFactory.createDto(
        payload.tenant?.toSerializedModel(),
      );

      const {
        getUserTenantSpecificUnifiedRoles,
        updateUserTenantSpecificUnifiedRoles,
        unifiedRolesEditorFallback,
        changeUserTenantSpecificAdminRolesType,
      } = this.userFactory.createUserUnifiedRolesTenantSpecificAggregate({
        userPrincipal,
        user,
        tenant,
      });

      const [getUserRolesState, getUserRolesHandler] =
        this.reactHookServicePort.composeUseApiRequestWrapper()(
          getUserTenantSpecificUnifiedRoles,
        );

      const [updateUserRolesState, updateUserRolesHandler] =
        this.reactHookServicePort.composeUseApiRequestWrapper()(
          updateUserTenantSpecificUnifiedRoles,
        );

      const hasRequiredApiRequestParameters =
        isUUID(tenant?.source.getTenantUuid()) &&
        isEmail(user?.source.getUsername());

      const isTenantSpecificRolesApiRequestInProgress =
        getUserRolesState.isLoading ||
        updateUserRolesState.isLoading ||
        !hasRequiredApiRequestParameters;

      const isAuthorized = userPrincipal.hasAnyOfPermissions([
        <Authority>'system-admin:read:tenant-specific-user-role-assignments',
      ]);

      const isUserRoleStateDefined = getUserRolesState.data === undefined;

      React.useEffect(() => {
        if (
          isUserRoleStateDefined &&
          !getUserRolesState.isLoading &&
          hasRequiredApiRequestParameters &&
          isAuthorized
        ) {
          getUserRolesHandler();
        }
      }, [
        isUserRoleStateDefined,
        isAuthorized,
        hasRequiredApiRequestParameters,
        getUserRolesState.isLoading,
        getUserRolesHandler,
      ]);

      const uiUserTenantSpecificRoles: TUIUserTenantSpecificRoles = {
        user,
        userPrincipal,
        isTenantSpecificRolesApiRequestInProgress,
        userTenantSpecificRolesEditor:
          getUserRolesState.data || unifiedRolesEditorFallback,
        changeUserTenantSpecificAdminRolesType,
        async saveUserTenantSpecificRoles(updatedUserAssignedUnifiedRoles) {
          await updateUserRolesHandler(updatedUserAssignedUnifiedRoles);
          await getUserRolesHandler();
        },
      };

      return uiUserTenantSpecificRoles;
    };
  }

  public composeUseUserTenantAgnosticRoles(): TUseUserTenantAgnosticRoles {
    return (payload) => {
      const { userPrincipal } = payload;
      const user = this.userFactory.createUserDto(
        payload.user?.toSerializedModel(),
      );
      const tenant = this.tenantFactory.createDto(
        payload.tenant?.toSerializedModel(),
      );

      const {
        getUserTenantAgnosticUnifiedRoles,
        updateUserTenantAgnosticUnifiedRoles,
        unifiedRolesEditor,
      } = this.userFactory.createUserUnifiedRolesTenantAgnosticAggregate({
        userPrincipal,
        user,
        tenant,
      });

      const [getUserRolesState, getUserRolesHandler] =
        this.reactHookServicePort.composeUseApiRequestWrapper()(
          getUserTenantAgnosticUnifiedRoles,
        );

      const [updateUserRolesState, updateUserRolesHandler] =
        this.reactHookServicePort.composeUseApiRequestWrapper()(
          updateUserTenantAgnosticUnifiedRoles,
        );

      const isUserCandidateForTenantAgnosticRoles =
        UserMapper.isUserCandidateForTenantAgnosticRoles(user);

      const hasRequiredApiRequestParameters =
        isUUID(tenant?.source.getTenantUuid()) &&
        isEmail(user?.source.getUsername());

      const userPrincipalHasPermissions = userPrincipal.hasAnyOfPermissions([
        <Authority>'system-admin:read:tenant-agnostic-user-role-assignments',
      ]);

      const isTenantAgnosticRolesApiRequestInProgress =
        getUserRolesState.isLoading ||
        updateUserRolesState.isLoading ||
        !hasRequiredApiRequestParameters;

      const isReadyForApiCall: boolean = Array.from([
        isUserCandidateForTenantAgnosticRoles,
        getUserRolesState.data === undefined,
        !getUserRolesState.isLoading,
        hasRequiredApiRequestParameters,
        userPrincipalHasPermissions,
      ]).every((booleanValueInCollection) => booleanValueInCollection);

      React.useEffect(() => {
        if (isReadyForApiCall) {
          getUserRolesHandler();
        }
      }, [getUserRolesHandler, isReadyForApiCall]);

      const userTenantAgnosticRoles: TUIUserTenantAgnosticRoles = {
        user,
        userPrincipal,
        shouldDisplayTenantAgnosticRoles:
          userPrincipalHasPermissions && isUserCandidateForTenantAgnosticRoles,
        isTenantAgnosticRolesApiRequestInProgress,
        userTenantAgnosticRolesEditor:
          getUserRolesState.data || unifiedRolesEditor,
        async saveUserTenantAgnosticRoles(updatedUserAssignedUnifiedRoles) {
          await updateUserRolesHandler(updatedUserAssignedUnifiedRoles);
          await getUserRolesHandler();
        },
      };

      return userTenantAgnosticRoles;
    };
  }
}
